import { ReactElement } from 'react'

import { useAtomValue } from 'jotai'

import {
  SequenceMenu,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { RedRed60 } from '@sequencehq/core-components'

import {
  isSavePendingAtom,
  invoiceIdAtom,
  invoiceStatusAtom,
  invoicePaymentStatusAtom,
  invoiceGoCardlessIntegrationPaymentStatusAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { usePermissions } from 'lib/rbac'

export const MenuItemVoidInvoice = () => {
  const isSavePending = useAtomValue(isSavePendingAtom)
  const paymentStatus = useAtomValue(invoicePaymentStatusAtom)
  const goCardlessStatus = useAtomValue(
    invoiceGoCardlessIntegrationPaymentStatusAtom
  )
  const { can } = usePermissions()

  if (!can('org.invoice:update')) {
    return null
  }

  // Only allow voiding unpaid invoices
  const isPaidOrPartiallyPaid =
    paymentStatus === 'PAID' || paymentStatus === 'PARTIALLY_PAID'
  const isGoCardlessPaymentInProgress = goCardlessStatus === 'IN_PROGRESS'

  return (
    <DialogVoidInvoice
      trigger={
        <SequenceMenu.Item
          uuid="void-invoice"
          label="Void invoice"
          style={{ color: RedRed60 }}
          isDisabled={
            isSavePending ||
            isPaidOrPartiallyPaid ||
            isGoCardlessPaymentInProgress
          }
          tooltip={
            isGoCardlessPaymentInProgress
              ? 'Voiding an invoice is disabled when a GoCardless payment is in progress'
              : undefined
          }
          data-testid="header.kebab-menu.void-invoice"
        />
      }
    />
  )
}

const DialogVoidInvoice = ({ trigger }: { trigger: ReactElement }) => {
  const { displayNotification } = useNotifications()

  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceStatus = useAtomValue(invoiceStatusAtom)
  const isSentInvoice = invoiceStatus === 'SENT'

  const voidInvoiceMutation = useMutation(
    dashboard20240730Client.postVoidInvoice,
    {
      onSuccess: () => {
        displayNotification('Invoice voided', { type: 'success' })
      },
      onError: () => {
        displayNotification('Failed to void invoice', {
          type: 'error'
        })
      }
    }
  )

  return (
    <SimpleModal
      data-testid="modal.void-invoice"
      trigger={trigger}
      variant="dialog"
      title={isSentInvoice ? 'Void sent invoice' : 'Void invoice'}
      submitButtonText="Void invoice"
      submittingButtonText="Voiding..."
      isDestructive
      onSubmit={async () => {
        if (!invoiceId) return false
        const res = await voidInvoiceMutation.mutateAsync({
          id: invoiceId
        })
        return Boolean(res)
      }}
    >
      {isSentInvoice
        ? 'Any payments made against this invoice will not be refunded and must be processed separately. This action cannot be undone.'
        : "You won't be able to make any further changes to this invoice after voiding."}
    </SimpleModal>
  )
}
